import { useEffect, useMemo, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let cache = null;

export function useRecruitmentCompanies() {
    const [items, setItems] = useState(cache || []);
    useEffect(() => {
        if (cache) return;
        fetch(`${API}/recruitment-companies`)
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => {
                cache = d;
                setItems(d);
            })
            .catch(() => setItems([]));
    }, []);
    return items;
}

export default function CompanyAutocomplete({ value, onChange, onMatch, className }) {
    const companies = useRecruitmentCompanies();
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const wrap = useRef(null);

    const matches = useMemo(() => {
        const term = value.trim().toUpperCase();
        if (!term) return [];
        return companies.filter((c) => c.name.toUpperCase().startsWith(term)).slice(0, 12);
    }, [companies, value]);

    const isMatch = (v) => companies.some((c) => c.name.toLowerCase() === v.trim().toLowerCase());

    useEffect(() => {
        const close = (e) => wrap.current && !wrap.current.contains(e.target) && setOpen(false);
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const pick = (name) => {
        onChange(name);
        onMatch(true);
        setOpen(false);
    };

    const onKey = (e) => {
        if (!open || matches.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % matches.length); }
        if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % matches.length); }
        if (e.key === "Enter") { e.preventDefault(); pick(matches[active].name); }
        if (e.key === "Escape") setOpen(false);
    };

    return (
        <div ref={wrap} className="relative">
            <input
                value={value}
                onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(0); onMatch(isMatch(e.target.value)); }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKey}
                placeholder="Recruitment Details"
                maxLength={255}
                autoComplete="off"
                aria-label="Employer Recruitment Company"
                aria-autocomplete="list"
                aria-expanded={open && matches.length > 0}
                className={className}
                data-testid="join-company-input"
            />
            {open && matches.length > 0 && (
                <ul role="listbox" className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-border bg-surface py-1 shadow-xl" data-testid="join-company-list">
                    {matches.map((c, i) => (
                        <li
                            key={c.id}
                            role="option"
                            aria-selected={i === active}
                            onMouseDown={() => pick(c.name)}
                            onMouseEnter={() => setActive(i)}
                            data-testid={`join-company-option-${i}`}
                            className={`cursor-pointer px-4 py-2 text-sm ${i === active ? "bg-primary/10 text-primary" : ""}`}
                        >
                            {c.name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
